/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `CategoriesCatalog` for the Caintra Connect frontend.
 * @module pages/CategoriesCatalog
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../utils/logger.js";
import styles from "../styles/CategoriesCatalog.module.css";

import { ReactComponent as SearchIcon } from "../assets/icons/Search.svg";
import { refreshToken, signOut } from "../utils/common.js";
import { AppContext } from "../context/AppContext";
import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";

import { SvgIcon, Box, IconButton, TextField, Typography } from "@mui/material";
import ContactPageOutlinedIcon from "@mui/icons-material/ContactPageOutlined";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { use, useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { getSelectedOption } from "../utils/common.js";
import {
  DEFAULT_RESULTS_LIMIT,
  getResultsLimitOptions,
} from "../utils/resultsLimitOptions.js";
import { canAccessCategories } from "../utils/accessControl";
import useRequireAccess from "../hooks/useRequireAccess";

import CategoryCreationModal from "../modals/CategoryCreationModal/index.jsx";
import CategoryEditModal from "../modals/CategoryEditModal/index.jsx";

const CategoriesCatalog = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { allowed } = useRequireAccess(canAccessCategories);
  const { changePermissions } = use(AppContext);

  const resultsOptions = useMemo(
    () => getResultsLimitOptions(t),
    [t, i18n.language],
  );

  const [categoriesData, setCategoriesData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [resultsLimit, setResultsLimit] = useState(DEFAULT_RESULTS_LIMIT);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!!token && token.trim() !== "undefined") {
      getData();
    } else {
      logger.info("Error trying to get the token to retrieve the users data.");
    }
  }, []);

  const getData = async () => {
    try {
      await getUserRoleData();
      await getCategories();
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
    }
  };

  const getUserRoleData = async () => {
    const tokenToUse = localStorage.getItem("token");
    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${tokenToUse}`,
      },
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_ROOT_API}permission/${localStorage.getItem(
          "userId",
        )}`,
        options,
      );
      const roleData = await response.json();
      const isTokenExpired =
        roleData?.message === "The incoming token has expired" ||
        roleData?.message === "The incoming token has expired or is invalid";
      if (isTokenExpired) {
        const newTokens = await refreshToken();
        if (!newTokens) {
          await signOut();
          navigate("/");
          return;
        }
        await getUserRoleData();
        return;
      }
      if (response.ok && roleData?.permisos != null) {
        changePermissions(roleData.permisos);
      }
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
    }
  };

  const getCategories = async () => {
    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_ROOT_API}category`,
        options,
      );
      const data = await response.json();
      if (data?.message === "The incoming token has expired") {
        const newTokens = await refreshToken();
        if (!newTokens) {
          await signOut();
          navigate("/");
          return;
        }
        await getCategories();
        return;
      }
      setCategoriesData(response.ok && Array.isArray(data) ? data : []);
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
    }
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleEditClick = (category) => {
    setSelectedCategory(category);
    setEditModalOpen(true);
  };

  const handleUpdateCategory = async (updatedCategory) => {
    setSelectedCategory(updatedCategory);
    await getCategories();
    // setCategoriesData((prevCategories) =>
    //   prevCategories.map((category) =>
    //     category._id === updatedCategory._id ? updatedCategory : category
    //   )
    // );
    // setSelectedCategory(updatedCategory);
  };

  const handleResultsLimitChange = (selectedOption) => {
    setResultsLimit(selectedOption.value);
    setCurrentPage(1);
  };

  const handleAddCategory = (newCategory) => {
    setCategoriesData((prevCategories) => {
      if (!Array.isArray(prevCategories)) {
        return [newCategory];
      }
      return [...prevCategories, newCategory];
    });
  };

  const handlePageChange = (direction) => {
    if (
      direction === "next" &&
      currentPage < Math.ceil(categoriesData.length / resultsLimit)
    ) {
      setCurrentPage((prevPage) => prevPage + 1);
    } else if (direction === "prev" && currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    } else {
      // Unsupported direction or already at bounds. No action required.
    }
  };

  const filteredCategoriesList = categoriesData.filter((category) =>
    category.nombre
      ? category.nombre.toLowerCase().includes(searchQuery.toLowerCase())
      : false,
  );

  const paginatedCategoriesList = filteredCategoriesList.slice(
    (currentPage - 1) * resultsLimit,
    currentPage * resultsLimit,
  );

  if (!allowed) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.languages}>
          <NotificationBell navigate={navigate} />
          <LanguageSelector />
        </div>

        {/* Page title and search bar */}
        <div className={styles.headerInfoContainer}>
          <div className={styles.headerInfo}>
            <SvgIcon sx={{ fontSize: "32px" }} viewBox="0 0 24 24">
              <defs>
                <linearGradient
                  id="gradient1"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#4eabe7" />
                  <stop offset="100%" stopColor="#2f59df" />
                </linearGradient>
              </defs>
              <ContactPageOutlinedIcon sx={{ fill: "url(#gradient1)" }} />
            </SvgIcon>
            <h1 className={styles.title}>{t("pages.categoriesCatalog.header")}</h1>
          </div>
          <div className={styles.searchSection}>
            <div className={styles.searchBarContainer}>
              <div className={styles.searchBar}>
                <div style={{ position: "absolute", left: "1%" }}>
                  <SvgIcon
                    sx={{ fontSize: "16px", color: "#3B3B3D" }}
                    component={SearchIcon}
                  />
                </div>
                <input
                  placeholder={t("pages.categoriesCatalog.searchPlaceholder")}
                  type="search"
                  value={searchQuery}
                  onChange={handleSearch}
                />
              </div>
              <div className={styles.searchBarIcon}>
                <SvgIcon
                  sx={{ height: "1.3rem", color: "#FCFCFC" }}
                  component={SearchIcon}
                />
              </div>
            </div>
            <div
              className={styles.newUserButton}
              onClick={() => setCreateModalOpen(true)}
            >
              <SvgIcon component={AddIcon} fontSize="small" />
              {t("pages.categoriesCatalog.create")}
            </div>

            {/* Modal Dialog */}
            <CategoryCreationModal
              open={createModalOpen}
              onClose={() => setCreateModalOpen(false)}
              onCreateCategory={handleAddCategory}
            />
          </div>
        </div>
      </div>

      {/* Categories table */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t("pages.categoriesCatalog.tableCategory")}</th>
              <th>{t("pages.categoriesCatalog.tableSubcategories")}</th>
              <th>{t("pages.categoriesCatalog.tableActions")}</th>
            </tr>
          </thead>
          <tbody>
            {paginatedCategoriesList.map((category, index) => (
              <tr key={index}>
                <td className={styles.userName}>{category.nombre}</td>
                <td>
                  {category?.subcategorias.filter((s) => s.activo).length > 0
                    ? category?.subcategorias
                        .filter((s) => s.activo)
                        .map((subcategoria) => {
                          return subcategoria.nombre;
                        })
                        .join(" / ")
                    : t("pages.categoriesCatalog.noSubcategory")}
                </td>
                <td>
                  <EditIcon
                    className={styles.actionsIcon}
                    onClick={() => handleEditClick(category)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.footerContainer}>
        <div className={styles.footer}>
          {/* Number of results dropdown */}
          <div className={styles.resultsSelector}>
            <Select
              options={resultsOptions}
              value={getSelectedOption(resultsOptions, resultsLimit)}
              isSearchable={false}
              menuPlacement="auto"
              onChange={handleResultsLimitChange}
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  border: "1px solid #BDBDBD",
                  borderRadius: "8px",
                  padding: "4px 8px",
                  boxShadow: "none",
                  "&:hover": {
                    borderColor: "#888",
                    cursor: "pointer",
                  },
                }),
                container: (baseStyles) => ({
                  ...baseStyles,
                  width: "192px",
                }),
                valueContainer: (baseStyles) => ({
                  ...baseStyles,
                  padding: "0 8px",
                }),
                singleValue: (baseStyles) => ({
                  ...baseStyles,
                  color: "#333",
                  fontSize: "16px",
                }),
                dropdownIndicator: (baseStyles) => ({
                  ...baseStyles,
                  color: "#333",
                  paddingRight: "8px",
                  "&:hover": {
                    color: "#111",
                  },
                }),
                indicatorSeparator: () => ({
                  display: "none",
                }),
                option: (baseStyles) => ({
                  ...baseStyles,
                  cursor: "pointer",
                }),
                menu: (baseStyles) => ({
                  ...baseStyles,
                  zIndex: 9999,
                }),
              }}
            />
          </div>

          {/* Page selector */}
          <div className={styles.pageSelectorContainer}>
            <div styles={styles.pageSelector}>
              <Box display="flex" alignItems="center" gap={1}>
                <IconButton onClick={() => handlePageChange("prev")}>
                  <ChevronLeftIcon />
                </IconButton>

                <Typography variant="body1">
                  {t("components.pageSelector.page")}
                </Typography>

                <TextField
                  value={currentPage}
                  size="small"
                  sx={{
                    width: 50,
                    textAlign: "center",
                    backgroundColor: "#fefefe",
                    "& input": { textAlign: "center", padding: "5px 10px" },
                  }}
                  readOnly
                />

                <Typography variant="body1">
                  {t("components.pageSelector.of")}{" "}
                  {Math.ceil(filteredCategoriesList.length / resultsLimit)}
                </Typography>

                <IconButton onClick={() => handlePageChange("next")}>
                  <ChevronRightIcon />
                </IconButton>
              </Box>
            </div>
          </div>
        </div>
      </div>

      {/* Display Modal */}

      {/* Edit Modal */}
      <CategoryEditModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        category={selectedCategory}
        onUpdateCategory={handleUpdateCategory}
      />
    </div>
  );
};

export default CategoriesCatalog;
